// src/pages/Resources.js

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GatedWhitepaper from '../components/GatedWhitepaper';
import csvFile from '../assets/whitepapers.csv';

const safeImageLoad = (imageName) => {
  try {
    return require(`../assets/${imageName}.png`);
  } catch (err) {
    try {
      return require(`../assets/${imageName}.jpg`);
    } catch (e) {
      return null;
    }
  }
};

function Resources() {
  const [whitepapers, setWhitepapers] = useState([]);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const parseCSV = (text) => {
      const result = [];
      let row = [];
      let inQuotes = false;
      let val = '';
      
      for (let i = 0; i < text.length; i++) {
        let char = text[i];
        let nextChar = text[i + 1];
        
        if (char === '"' && inQuotes && nextChar === '"') {
          val += '"'; 
          i++; 
        } else if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          row.push(val.trim()); 
          val = '';
        } else if ((char === '\n' || char === '\r') && !inQuotes) {
          if (char === '\r' && nextChar === '\n') i++; 
          row.push(val.trim()); 
          if (row.some(field => field !== '')) {
            result.push(row);
          }
          row = []; 
          val = '';
        } else {
          val += char;
        }
      }
      if (val || row.length > 0) {
        row.push(val.trim());
        result.push(row);
      }
      return result;
    };

    fetch(csvFile)
      .then(response => response.text())
      .then(csvText => {
        const rows = parseCSV(csvText);
        if (rows.length > 1) {
          const headers = rows[0];
          const parsedData = rows.slice(1).map(row => {
            let obj = {};
            headers.forEach((header, index) => {
              obj[header.trim()] = row[index] || '';
            });
            return obj;
          });
          
          const validData = parsedData.filter(p => p.Title);
          setWhitepapers(validData);

          // Handle routing logic from Home page (e.g., /resources?id=1)
          const queryParams = new URLSearchParams(location.search);
          const idFromUrl = queryParams.get('id');
          if (idFromUrl) {
            const paper = validData.find(p => p.Slno === idFromUrl);
            if (paper) setSelectedPaper(paper);
          }
        }
      })
      .catch(err => console.error("Error loading CSV:", err));
  }, [location.search]);

  const handleBackToGrid = () => {
    setSelectedPaper(null);
    navigate('/resources');
  };

  const handleSelectPaper = (paper) => {
    setSelectedPaper(paper);
    window.scrollTo(0, 0);
  };

  return (
    <div style={styles.pageWrapper}>
      <section style={styles.headerSection}>
        <h1 style={styles.pageTitle}>Latest Resources</h1>
        <p style={styles.pageSubtitle}>Discover the latest B2B strategies, insights and Whitepapers</p>
      </section>

      <section style={styles.contentSection}>
        {selectedPaper ? (
          <div>
            <button onClick={handleBackToGrid} style={styles.backButton}>
              &larr; Back to Resources
            </button>
            <GatedWhitepaper data={selectedPaper} />
          </div>
        ) : (
          <div style={styles.gridContainer}>
            {whitepapers.map((paper, index) => {
              const img1 = safeImageLoad(`${paper.Slno}_image_1`);
              
              return (
                <div key={index} style={styles.card}>
                  <div style={styles.cardImageWrapper}>
                    {img1 ? (
                      <img src={img1} alt={paper.Title} style={styles.cardImage} />
                    ) : (
                      <div style={styles.placeholderImage}>No Image</div>
                    )}
                  </div>
                  
                  <div style={styles.cardContent}>
                    <h3 style={styles.cardTitle}>{paper.Title}</h3>
                    {paper.Para1 && (
                      <p style={styles.cardDesc}>
                        {paper.Para1.length > 100 ? `${paper.Para1.substring(0, 100)}...` : paper.Para1}
                      </p>
                    )}
                    
                    <button 
                      onClick={() => handleSelectPaper(paper)} 
                      style={styles.downloadBtn}
                    >
                      Read Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

const styles = {
  pageWrapper: {
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
  },
  headerSection: {
    backgroundColor: '#FFFFFF',
    padding: '60px 5%',
    textAlign: 'center',
    borderBottom: '1px solid #E5E7EB'
  },
  pageTitle: {
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#00537A'
  },
  pageSubtitle: {
    fontSize: '18px',
    color: '#6B7280'
  },
  contentSection: {
    padding: '60px 5%',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '30px'
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  },
  cardImageWrapper: {
    width: '100%',
    height: '220px',
    overflow: 'hidden',
    backgroundColor: '#F3F4F6'
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9CA3AF'
  },
  cardContent: {
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  cardTitle: {
    fontSize: '20px',
    color: '#111827',
    fontWeight: 'bold',
    marginBottom: '15px',
    lineHeight: '1.4'
  },
  cardDesc: {
    fontSize: '15px',
    color: '#4B5563',
    lineHeight: '1.6',
    marginBottom: '25px',
    flex: 1
  },
  downloadBtn: {
    alignSelf: 'flex-start',
    backgroundColor: '#00537A',
    color: '#FFFFFF',
    border: 'none',
    padding: '12px 25px',
    borderRadius: '4px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  },
  backButton: {
    backgroundColor: 'transparent',
    color: '#00537A',
    border: 'none',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '30px',
    display: 'inline-flex',
    alignItems: 'center',
    padding: 0
  }
};

export default Resources;